/**
 * New In Products Carousel
 * Handles carousel sliding, progress indicators, and navigation
 */

class NewInProductsCarousel {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('.new-in-products__track');
    this.cards = section.querySelectorAll('.new-in-products__card');
    this.prevButton = section.querySelector('.new-in-products__arrow--prev');
    this.nextButton = section.querySelector('.new-in-products__arrow--next');
    this.progressBars = section.querySelectorAll('.new-in-products__progress-bar');
    
    // Get cards per slide based on screen size
    this.updateCardsPerSlide();
    
    this.currentSlide = 0;
    this.totalSlides = Math.ceil(this.cards.length / this.cardsPerSlide);
    
    this.init();
    
    // Update on resize
    window.addEventListener('resize', () => this.handleResize());
  }
  
  updateCardsPerSlide() {
    const width = window.innerWidth;
    
    // Determine cards per slide and gap based on breakpoints
    if (width >= 1920) {
      this.cardsPerSlide = 4;
      this.gap = 40;
    } else if (width >= 1600) {
      this.cardsPerSlide = 4;
      this.gap = 30;
    } else if (width >= 1280) {
      this.cardsPerSlide = 4;
      this.gap = 24;
    } else if (width >= 990) {
      this.cardsPerSlide = 3;
      this.gap = 20;
    } else if (width >= 750) {
      this.cardsPerSlide = 2;
      this.gap = 20;
    } else {
      this.cardsPerSlide = 1;
      this.gap = 0;
    }
    
    // Get actual card width from DOM (percentage-based)
    if (this.cards.length > 0) {
      this.cardWidth = this.cards[0].offsetWidth;
    }
  }
  
  handleResize() {
    this.updateCardsPerSlide();
    this.totalSlides = Math.ceil(this.cards.length / this.cardsPerSlide);
    this.currentSlide = Math.min(this.currentSlide, this.totalSlides - 1);
    this.updateCarousel();
  }

  init() {
    if (this.cards.length === 0) return;
    
    // Set up event listeners
    this.prevButton.addEventListener('click', () => this.prevSlide());
    this.nextButton.addEventListener('click', () => this.nextSlide());
    
    // Touch/swipe support
    this.setupTouchEvents();
    
    // Update initial state
    this.updateCarousel();
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.updateCarousel();
    }
  }

  nextSlide() {
    if (this.currentSlide < this.totalSlides - 1) {
      this.currentSlide++;
      this.updateCarousel();
    }
  }

  updateCarousel() {
    // Calculate translation using dynamic card width and gap
    const translateX = -(this.currentSlide * (this.cardWidth + this.gap) * this.cardsPerSlide);
    
    // Apply translation
    this.track.style.transform = `translateX(${translateX}px)`;
    
    // Update arrow states
    this.prevButton.disabled = this.currentSlide === 0;
    this.nextButton.disabled = this.currentSlide === this.totalSlides - 1;
    
    // Update progress bars
    this.updateProgress();
  }

  updateProgress() {
    // For now, simple 2-bar progress
    // First bar active on first half, second bar active on second half
    const midpoint = Math.ceil(this.totalSlides / 2);
    
    this.progressBars.forEach((bar, index) => {
      if (index === 0) {
        bar.classList.toggle('new-in-products__progress-bar--active', this.currentSlide < midpoint);
      } else if (index === 1) {
        bar.classList.toggle('new-in-products__progress-bar--active', this.currentSlide >= midpoint);
      }
    });
  }

  setupTouchEvents() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      
      const diff = startX - currentX;
      const threshold = 50; // Minimum swipe distance

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }

      isDragging = false;
    });
  }
}

// Initialize all carousels on page
document.addEventListener('DOMContentLoaded', () => {
  const carouselSections = document.querySelectorAll('.new-in-products');
  carouselSections.forEach(section => {
    new NewInProductsCarousel(section);
  });
});

// Reinitialize on Shopify section reload (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.new-in-products');
    if (section) {
      new NewInProductsCarousel(section);
    }
  });
}
